import {buildLinkMetadataUrl, normalizeLinkMetadata} from "./linkEmbedMetadata";

function hostFromUrl(url) {
    try {
        return new URL(url).hostname;
    } catch (_err) {
        return "link";
    }
}

function buildLinkEmbedContent(attrs) {
    const title = attrs.title || attrs.url || "Link";
    const description = attrs.description || attrs.url || "";
    const siteName = attrs.siteName || hostFromUrl(attrs.url);

    const root = document.createElement("div");
    root.className = "pm-link-embed__card";

    const titleEl = document.createElement("div");
    titleEl.className = "pm-link-embed__title";
    titleEl.textContent = title;

    const descriptionEl = document.createElement("div");
    descriptionEl.className = "pm-link-embed__description";
    descriptionEl.textContent = description;

    const metaEl = document.createElement("div");
    metaEl.className = "pm-link-embed__meta";
    metaEl.textContent = siteName;

    root.appendChild(titleEl);
    root.appendChild(descriptionEl);
    root.appendChild(metaEl);

    return root;
}

export class LinkEmbedNodeView {
    constructor(node, view, getPos) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.abortController = null;
        this.destroyed = false;
        this.dom = document.createElement("div");
        this.dom.className = "pm-link-embed";
        this.dom.setAttribute("contenteditable", "false");
        this.dom.setAttribute("data-url", node.attrs.url || "");

        this.render();
        this.maybeFetchMetadata();
    }

    render() {
        this.dom.innerHTML = "";
        this.dom.appendChild(buildLinkEmbedContent(this.node.attrs));
    }

    update(node) {
        if (node.type !== this.node.type) return false;
        this.node = node;
        this.dom.setAttribute("data-url", node.attrs.url || "");
        this.render();
        this.maybeFetchMetadata();
        return true;
    }

    selectNode() {
        this.dom.classList.add("ProseMirror-selectednode");
    }

    deselectNode() {
        this.dom.classList.remove("ProseMirror-selectednode");
    }

    stopEvent(event) {
        const target = event?.target;
        return target instanceof HTMLElement && target.closest(".pm-link-embed") != null;
    }

    ignoreMutation() {
        return true;
    }

    shouldFetchMetadata() {
        const url = this.node?.attrs?.url;
        if (!url) return false;

        const title = this.node?.attrs?.title;
        const description = this.node?.attrs?.description;

        const titleNeedsRefresh = !title || title === url;
        const descriptionNeedsRefresh = !description || description === url;
        return titleNeedsRefresh || descriptionNeedsRefresh;
    }

    async maybeFetchMetadata() {
        if (this.destroyed || !this.view || typeof this.getPos !== "function") return;
        if (!this.shouldFetchMetadata()) return;
        if (!this.node?.attrs?.url) return;

        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        try {
            const response = await fetch(buildLinkMetadataUrl(this.node.attrs.url), {
                method: "GET",
                signal: this.abortController.signal,
                headers: {Accept: "application/json"},
            });
            if (!response.ok) return;

            const payload = await response.json();
            const meta = normalizeLinkMetadata(payload, this.node.attrs.url);
            if (this.destroyed) return;

            let pos = null;
            try {
                pos = this.getPos();
            } catch (_err) {
                return;
            }
            if (typeof pos !== "number") return;

            const tr = this.view.state.tr.setNodeMarkup(pos, null, {
                ...this.node.attrs,
                ...meta,
            });
            this.view.dispatch(tr);
        } catch (err) {
            if (err?.name !== "AbortError") {
                // Keep node stable; metadata enrichment is best-effort.
                console.warn("[linkEmbedNodeView] metadata fetch failed", err);
            }
        }
    }

    destroy() {
        this.destroyed = true;
        if (this.abortController) {
            this.abortController.abort();
        }
    }
}

export function createLinkEmbedNodeViews() {
    return {
        link_embed: (node, view, getPos) => new LinkEmbedNodeView(node, view, getPos),
    };
}

